import { array, boolean, mixed, number, object, ObjectSchema, string } from 'yup';
import { EntityRef } from "../../shared/technical/rest/entities";
import { Locale } from "../../shared/technical/i18n/i18n";
import { Company } from "../companies/Company";
import { ApplicationRole, applicationRoleSchema } from "./ApplicationRoles";
import { TFunction } from "i18next";
import { formError } from "../../shared/components/forms/formUtils";
import { ValidityInfo, validityInfoSchema } from "../../shared/technical/domain/ValidityInfo";
import { AuditInfo, auditInfoSchema } from "./AuditInfo";
import { Group } from "./Group";
import { TermsAndConditionsAcceptance } from "./TermsAndConditionsAcceptance";
import { RoleName } from "./Role";

export interface User extends EntityRef {
  version?: number,
  enabled: boolean,
  emailVerified: boolean,
  email: string,
  firstName: string,
  lastName: string,
  locale: Locale,
  company?: Company,
  applicationRoles: ApplicationRole[],
  hasFilteredApplicationRoles?: boolean,
  /**
   * Only part of create and update responses: the account service roles the server set differently than requested
   */
  roleAdjustments?: RoleAdjustment[],
  validityInfo?: ValidityInfo,
  auditInfo?: AuditInfo,
  /**
   * Only sent when creating a user: if a user with this email already exists, the given application roles
   * are added to him instead of failing with USER_EXISTS_ALREADY.
   */
  upsert?: boolean
}

export interface RoleAdjustment {
  application: string,
  role: RoleName,
  type: 'ADDED' | 'REMOVED',
  reason: 'APPLICATION_ADMIN_DERIVED' | 'NO_ADMINISTERED_APPLICATION' | 'ACCOUNT_SERVICE_ADMIN_EXCLUSIVE' | 'NOT_PERMITTED'
}

export interface UserDetails extends User {
  groups: Group[],
  acceptedTermsAndConditions: TermsAndConditionsAcceptance[]
}

export const USER_LANGUAGES = [Locale.en, Locale.de, Locale.fr, Locale.it, Locale.es, Locale.nl, Locale.zh]

const PERSON_NAME_PATTERN = /^[^<>&"\v$%!#?§;*~\/\\|^=\[\]{}()\p{Cc}]+$/u;

export function userSchema(t: TFunction): ObjectSchema<User> {
  return object({
    id: string(),
    version: number(),
    enabled: boolean().default(true),
    emailVerified: boolean().default(false),
    email: string().trim().email(formError(t, 'mustBeValidEmail', 'User.')).required(userFieldDefinedError(t)),
    firstName: string().trim().matches(PERSON_NAME_PATTERN, {message: formError(t, 'containsProhibitedCharacters', 'User.') as any, excludeEmptyString: true}),
    lastName: string().trim().matches(PERSON_NAME_PATTERN, {message: formError(t, 'containsProhibitedCharacters', 'User.') as any, excludeEmptyString: true}),
    locale: string().default(Locale.en).defined(userFieldDefinedError(t)),
    company: mixed<Company>(),
    applicationRoles: array<any, ApplicationRole>(applicationRoleSchema(t)).default([{} as ApplicationRole]),
    validityInfo: validityInfoSchema(t),
    auditInfo: auditInfoSchema()
  }).defined() as unknown as ObjectSchema<User>
}

export interface LightweightUser extends EntityRef {
  email: string,
  firstName: string,
  lastName: string
}

function userFieldDefinedError(t: TFunction) {
  return formError(t, 'mustBeDefined', 'User.');
}

export function defaultUser(t: TFunction) {
  return userSchema(t).getDefault() as UserDetails
}

export function formatUser(user?: User, defaultUser: string = '') {
  if (!user) {
    return defaultUser
  }
  return user.firstName + ' ' + user.lastName
}
